import React from 'react'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'
import Icons from '@/scenes/Editor/components/Icons'

interface DragUploadOverlayProps {
  fullViewport?: boolean
  header?: string
  description?: string
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void
}

function DragUploadOverlay({
  fullViewport = false,
  header,
  description,
  onDrop,
  onDragLeave,
}: DragUploadOverlayProps) {
  const { t } = useTranslation()

  return (
    <div
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // `fixed` pins the overlay to the viewport so it stays put when the
        // underlying page is scrolled; `absolute` keeps inline drop zones
        // scoped to their own container.
        position: fullViewport ? 'fixed' : 'absolute',
        height: fullViewport ? '100vh' : '100%',
        inset: 0,
        zIndex: 9999,
      }}
      onDrop={onDrop}
      onDragEnter={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onDragOver={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onDragLeave={onDragLeave}
    >
      <div
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          marginTop: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#FF0560',
          pointerEvents: 'none',
          height: '100%',
        }}
      >
        <div
          style={{
            position: 'relative',
            height: '106px',
            width: '100%',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              animation: 'moveToTop .5s linear',
            }}
          >
            <Icons.DropImage />
            <style>
              {`
              @keyframes moveToTop {
                from {
                  margin-top: 50px;
                }
                to {
                  margin-top: 0px;
                }
              }
              @keyframes fade {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }
              `}
            </style>
          </div>
        </div>
        <div
          style={{
            ...lightTheme.typography.TitleBold,
            margin: '24px 0 16px 0',
            color: '#FF0560',
            animation: 'fade .5s linear',
          }}
        >
          {header ?? t('Drop to upload your file')}
        </div>
        <p
          style={{
            fontSize: '16px',
            fontWeight: '600',
            lineHeight: '30px',
            margin: 0,
            width: '330px',
            textAlign: 'center',
            animation: 'fade .5s linear',
          }}
        >
          {description ?? t('Files for upload should be .jpg, .png, or .heic and 10 MB or less.')}
        </p>
      </div>
    </div>
  )
}

export default React.memo(DragUploadOverlay)
